import { useRef, useState } from 'react';
import { Box, CircularProgress, Modal, Stack, TextField, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import { aiListingAutofillProvider } from '../../providers/aiListingAutofillProvider';
import { propertyAdEditorProvider } from '../../providers/propertyAdEditorProvider';
import type { AiListingAutofillResponse } from '../../types/aiListingAutofill';
import type { PropertyAdInProgressAutosaveFields } from '../../types/propertyAdEditor';
import type { ResponseType } from '../../types/responses';

import { MyButton, SectionHeader } from '../uiComponents';
import { buildAiListingFormPatch } from './aiListingFormPatch';
import type { AiListingEditorNavigationState } from './aiListingNavigation';
import { EditorSection } from './EditorSection';
import styles from './PropertyAdEditor.module.scss';

const MAX_SOURCE_TEXT_LENGTH = 12000;
const SAVE_ERROR_MESSAGE = "We couldn't save the generated listing. Please try again.";

interface GeneratedListing {
  response: AiListingAutofillResponse;
  patch: PropertyAdInProgressAutosaveFields;
}

type FlowState =
  | { status: 'idle' }
  | { status: 'generating' }
  | { status: 'saving'; generated: GeneratedListing }
  | { status: 'aiError'; message: string }
  | { status: 'saveError'; generated: GeneratedListing; message: string };

const getRequestErrorMessage = (response: ResponseType<AiListingAutofillResponse>): string => {
  if (response.status === 401) return 'Please sign in and try again.';
  if (response.status === 403) return 'You do not have permission to generate this listing.';

  switch (response.code) {
    case 'invalid_request':
      return 'Please check the listing text and try again.';
    case 'invalid_model_result':
      return "We couldn't understand this listing. Please try again.";
    case 'model_provider_error':
      return 'AI service is temporarily unavailable. Please try again.';
    case 'location_resolution_error':
      return 'The listing was processed, but location matching is temporarily unavailable. Please try again.';
    case 'internal_error':
      return 'Something went wrong. Please try again.';
    default:
      return 'Something went wrong. Please try again.';
  }
};

export const TextListingEntry = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [flow, setFlow] = useState<FlowState>({ status: 'idle' });
  const flowInFlightRef = useRef(false);
  const isBusy = flow.status === 'generating' || flow.status === 'saving';
  const isTooLong = text.length > MAX_SOURCE_TEXT_LENGTH;
  const canGenerate = text.trim().length > 0 && !isTooLong && !isBusy;

  const handleTextChange = (value: string) => {
    if (flowInFlightRef.current) return;
    setText(value);
    setFlow({ status: 'idle' });
  };

  const saveGenerated = async (generated: GeneratedListing) => {
    setFlow({ status: 'saving', generated });

    try {
      const current = await propertyAdEditorProvider.getInProgressWorkspace();
      if (!current.ok || !current.data || current.data.id !== id) {
        setFlow({ status: 'saveError', generated, message: SAVE_ERROR_MESSAGE });
        return;
      }

      const saved = await propertyAdEditorProvider.autosaveInProgressWorkspace({
        ...generated.patch,
        expectedVersion: current.data.version,
      });
      if (!saved.ok || !saved.data || saved.data.id !== id) {
        setFlow({ status: 'saveError', generated, message: SAVE_ERROR_MESSAGE });
        return;
      }

      const navigationState: AiListingEditorNavigationState = {
        aiAutofillWorkspaceId: current.data.id,
        aiAutofillReview: generated.response.review,
        aiAutofillLocationStatus: generated.response.location.status,
      };
      navigate('/ads/create', { state: navigationState });
    } catch {
      setFlow({ status: 'saveError', generated, message: SAVE_ERROR_MESSAGE });
    }
  };

  const handleGenerate = async () => {
    if (flowInFlightRef.current || flow.status === 'saveError' || !canGenerate) return;

    flowInFlightRef.current = true;
    setFlow({ status: 'generating' });

    try {
      const result = await aiListingAutofillProvider.autofillListingFromText(text);
      if (!result.ok || !result.data) {
        setFlow({ status: 'aiError', message: getRequestErrorMessage(result) });
        return;
      }

      const patch = buildAiListingFormPatch(result.data);
      if (Object.keys(patch).length === 0) {
        setFlow({
          status: 'aiError',
          message: "We couldn't understand this listing. Please try again.",
        });
        return;
      }

      await saveGenerated({ response: result.data, patch });
    } catch {
      setFlow({ status: 'aiError', message: 'Something went wrong. Please try again.' });
    } finally {
      flowInFlightRef.current = false;
    }
  };

  const handleRetry = async () => {
    if (flowInFlightRef.current || flow.status !== 'saveError') return;

    flowInFlightRef.current = true;
    try {
      await saveGenerated(flow.generated);
    } finally {
      flowInFlightRef.current = false;
    }
  };

  return (
    <Box className={styles.editorFrame} component='section' data-workspace-id={id}>
      <Modal
        aria-labelledby='ai-listing-loading-label'
        disableEscapeKeyDown
        open={isBusy}
        slotProps={{ backdrop: { id: 'ai-listing-blocking-backdrop' } }}
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
          zIndex: (theme) => theme.zIndex.tooltip + 1,
        }}
      >
        <Stack alignItems='center' color='common.white' role='status' spacing={2}>
          <CircularProgress color='inherit' />
          <Typography component='p' id='ai-listing-loading-label' variant='h6'>
            {flow.status === 'generating' ? 'Generating listing...' : 'Preparing your listing...'}
          </Typography>
        </Stack>
      </Modal>
      <SectionHeader title='Create a new listing' />
      <Box className={styles.editorLayout} paddingY={{ xs: 3, sm: 4 }}>
        <Stack alignItems='flex-start' minWidth={0} spacing={2.5} width='100%'>
          <EditorSection
            description='Paste property details from WhatsApp, Telegram or another message.'
            title='Paste listing text'
            titleId='text-listing-entry-title'
          >
            <TextField
              disabled={isBusy}
              error={isTooLong}
              fullWidth
              inputProps={{ 'aria-label': 'Listing text' }}
              multiline
              onChange={(event) => handleTextChange(event.target.value)}
              placeholder='Paste property details here'
              rows={10}
              sx={{ '& .MuiInputBase-input::placeholder': { opacity: 0.6 } }}
              value={text}
            />
            <Box display='flex' justifyContent='flex-end' marginTop={1}>
              <Typography
                aria-live='polite'
                color={isTooLong ? 'error' : 'text.secondary'}
                component='p'
                variant='body2'
              >
                {text.length} / {MAX_SOURCE_TEXT_LENGTH}
              </Typography>
            </Box>
            {isTooLong && (
              <Typography color='error' component='p' role='alert' variant='body2'>
                Listing text must be 12,000 characters or fewer.
              </Typography>
            )}
          </EditorSection>
          <Stack aria-label='Listing actions' component='section' spacing={1.5} width='100%'>
            <Stack
              alignItems={{ xs: 'stretch', sm: 'center' }}
              direction={{ xs: 'column', sm: 'row' }}
              flexWrap={{ sm: 'wrap' }}
              gap={2.5}
            >
              <MyButton
                data={{
                  buttonName: flow.status === 'saveError' ? 'Retry' : 'Generate listing',
                  buttonType: 'button',
                  customWidth: '190px',
                  variant: 'contained',
                }}
                disabled={isBusy || (flow.status !== 'saveError' && !canGenerate)}
                onClick={() =>
                  void (flow.status === 'saveError' ? handleRetry() : handleGenerate())
                }
              />
              <MyButton
                data={{
                  buttonName: 'Back',
                  buttonType: 'button',
                  customWidth: '150px',
                  styleType: 'cancel',
                  variant: 'outlined',
                }}
                disabled={isBusy}
                onClick={() => navigate('/ads/create')}
              />
            </Stack>
            {flow.status === 'aiError' && (
              <Typography color='error' component='p' role='alert' variant='body2'>
                {flow.message}
              </Typography>
            )}
            {flow.status === 'saveError' && (
              <Typography color='error' component='p' role='alert' variant='body2'>
                {flow.message}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};
